import { useState, useEffect } from "react";
import {
  Bell,
  Check,
  ExternalLink,
  FileText,
  Home,
  Wrench,
  CreditCard,
  Mail,
  CheckCircle,
  XCircle,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { formatDistanceToNow } from "date-fns";

import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useAuth } from "@/components/auth-provider";
import { Notification } from "@/types/types";
import {
  subscribeToNotifications,
  markNotificationAsRead,
  markAllNotificationsAsRead,
} from "@/lib/notification-service";

export function NotificationBell() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!user?.id) return;

    // Subscribe to real-time notifications
    const unsubscribe = subscribeToNotifications(
      user.id,
      (updatedNotifications) => {
        setNotifications(updatedNotifications);
        const unread = updatedNotifications.filter((n) => !n.read).length;
        setUnreadCount(unread);
      }
    );

    return () => unsubscribe();
  }, [user?.id]);

  async function handleNotificationClick(notification: Notification) {
    if (!notification.read) {
      await markNotificationAsRead(notification.id);
    }

    setOpen(false);

    if (notification.actionUrl) {
      // Validate and fix old notification URLs
      let url = notification.actionUrl;

      // Fix old tenant lease URLs
      if (url.includes("/tenant/lease/")) {
        url = "/tenant/dashboard?tab=lease";
      }
      // Fix old tenant payment URLs
      else if (url === "/tenant/payments") {
        url = "/tenant/dashboard?tab=payments";
      }
      // Fix old tenant application URLs
      else if (url.includes("/tenant/applications")) {
        url = "/tenant/dashboard";
      }

      navigate(url);
    }
  }

  async function handleMarkAllRead() {
    if (!user?.id) return;
    await markAllNotificationsAsRead(user.id);
  }

  function getNotificationIcon(type: string) {
    switch (type) {
      case "lease_created":
        return <FileText className="h-5 w-5 text-blue-500" />;
      case "application_approved":
        return <CheckCircle className="h-5 w-5 text-green-500" />;
      case "application_rejected":
        return <XCircle className="h-5 w-5 text-red-500" />;
      case "maintenance_request":
        return <Wrench className="h-5 w-5 text-orange-500" />;
      case "payment_due":
        return <CreditCard className="h-5 w-5 text-yellow-500" />;
      case "property_approved":
        return <Home className="h-5 w-5 text-green-500" />;
      case "property_rejected":
        return <Home className="h-5 w-5 text-red-500" />;
      case "message_received":
        return <Mail className="h-5 w-5 text-purple-500" />;
      default:
        return <Bell className="h-5 w-5 text-muted-foreground" />;
    }
  }

  if (!user) return null;

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="relative">
          <Bell className="h-5 w-5" />
          {unreadCount > 0 && (
            <Badge
              variant="destructive"
              className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0 text-xs"
            >
              {unreadCount > 9 ? "9+" : unreadCount}
            </Badge>
          )}
          <span className="sr-only">Notifications</span>
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent align="end" className="w-[380px]">
        <DropdownMenuLabel className="flex items-center justify-between">
          <span>Notifications</span>
          {unreadCount > 0 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={handleMarkAllRead}
              className="h-auto p-0 text-xs text-muted-foreground hover:text-foreground"
            >
              <Check className="h-3 w-3 mr-1" />
              Mark all read
            </Button>
          )}
        </DropdownMenuLabel>

        <DropdownMenuSeparator />

        <ScrollArea className="h-[400px]">
          {notifications.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-8 text-center">
              <Bell className="h-12 w-12 text-muted-foreground opacity-50 mb-3" />
              <p className="text-sm text-muted-foreground">No notifications</p>
            </div>
          ) : (
            notifications.map((notification) => (
              <DropdownMenuItem
                key={notification.id}
                className="flex flex-col items-start gap-2 p-4 cursor-pointer"
                onClick={() => handleNotificationClick(notification)}
              >
                <div className="flex items-start gap-3 w-full">
                  <div className="mt-0.5">
                    {getNotificationIcon(notification.type)}
                  </div>

                  <div className="flex-1 space-y-1">
                    <div className="flex items-start justify-between gap-2">
                      <p
                        className={`text-sm leading-tight ${
                          notification.read
                            ? "text-muted-foreground"
                            : "font-medium text-foreground"
                        }`}
                      >
                        {notification.title}
                      </p>
                      {!notification.read && (
                        <div className="h-2 w-2 rounded-full bg-blue-600 shrink-0 mt-1" />
                      )}
                    </div>

                    <p className="text-xs text-muted-foreground line-clamp-2">
                      {notification.message}
                    </p>

                    <div className="flex items-center justify-between">
                      <p className="text-xs text-muted-foreground">
                        {formatDistanceToNow(new Date(notification.createdAt), {
                          addSuffix: true,
                        })}
                      </p>

                      {notification.actionUrl && (
                        <ExternalLink className="h-3 w-3 text-muted-foreground" />
                      )}
                    </div>
                  </div>
                </div>
              </DropdownMenuItem>
            ))
          )}
        </ScrollArea>

        {notifications.length > 0 && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              className="justify-center text-sm font-medium cursor-pointer"
              onClick={() => {
                setOpen(false);
                navigate("/dashboard/notifications");
              }}
            >
              View all notifications
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
